import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, X, ChevronRight } from "lucide-react";
import { isIOSSafari, isStandalone, isPushSupported } from "@/lib/push";

const DISMISS_KEY = "aimka_ios_install_dismissed_v1";

/**
 * Persistent banner that reminds iPhone/iPad users to install AIMKA to their
 * home screen before enabling notifications. Silently hidden when:
 *  - device is not iOS Safari
 *  - PWA is already installed (standalone)
 *  - the user tapped the dismiss (until next login)
 */
export default function IOSInstallBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  const [ready, setReady] = useState(false);

  useEffect(() => { setReady(true); }, []);
  if (!ready) return null;

  const iOS = isIOSSafari();
  const standalone = isStandalone();
  const supported = isPushSupported();

  // Hide when installed, not iOS, or dismissed. Show even if push isn't
  // supported on the current tab — installing to home-screen is what fixes it.
  if (!iOS || standalone || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-black text-white px-4 py-2.5 flex items-start gap-3 text-[12px] leading-snug"
      data-testid="ios-install-banner"
      role="status"
    >
      <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="font-mono uppercase tracking-widest text-[10px] opacity-80">
          iPhone / iPad · installe AIMKA
        </div>
        <div className="mt-0.5">
          Pour recevoir les notifications : icône{" "}
          <b>Partager</b> en bas de Safari &rarr; <b>« Sur l&apos;écran d&apos;accueil »</b>{" "}
          {supported ? "" : "(les notifications ne marcheront pas dans cet onglet Safari, uniquement depuis l'app installée)"}.
        </div>
        <Link
          to="/veille/reglages"
          data-testid="ios-banner-details"
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest underline underline-offset-2"
        >
          Voir les réglages <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <button
        onClick={dismiss}
        data-testid="ios-banner-dismiss"
        aria-label="Masquer"
        className="min-h-[36px] min-w-[36px] flex items-center justify-center border border-white/40 hover:bg-white/10"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
